import { useMutation } from '@apollo/client'
import {
  CalculatePerDiemDocument,
  CalculatePerDiemMutation,
  CalculatePerDiemMutationVariables,
} from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'

import { CalculationResult } from 'modules/expenses/components/expenses/New/PerDiemWizard/form/context'
import { Destination } from 'modules/expenses/components/expenses/New/PerDiemWizard/form/types'

interface CalculatePerDiemParams {
  employeeId: number
  startDate: Date | null
  endDate: Date | null
  from?: string
  destinations: Destination[]
}

export const useCalculatePerDiem = () => {
  const [calculateMutation, { loading }] = useMutation<
    CalculatePerDiemMutation,
    CalculatePerDiemMutationVariables
  >(CalculatePerDiemDocument)

  const calculate = useUnsafeCallback(
    async (params: CalculatePerDiemParams): Promise<Partial<CalculationResult>> => {
      if (!params.startDate || !params.endDate || params.destinations.length === 0) {
        return { totalAmountCents: 0, isCalculating: false }
      }

      // Check if all destinations have a country
      const validDestinations = params.destinations.filter(
        (dest) => dest.locationData.country && dest.arrivalDatetime
      )

      if (validDestinations.length === 0) {
        return { totalAmountCents: 0, isCalculating: false }
      }

      const destinations = validDestinations.map((dest) => ({
        arrivalDatetime: dest.arrivalDatetime.toISOString(),
        locationData: {
          country: dest.locationData.country,
          administrativeAreaLevel1: dest.locationData.administrativeAreaLevel1,
          administrativeAreaLevel2: dest.locationData.administrativeAreaLevel2,
          locality: dest.locationData.locality,
        },
      }))

      const variables: CalculatePerDiemMutationVariables = {
        employeeId: params.employeeId,
        startDate: params.startDate.toISOString(),
        from: params.from,
        destinations,
      }

      try {
        const result = await calculateMutation({
          variables,
        })

        const perDiem = result.data?.expensesMutations?.calculateExpensesPerDiem?.perDiem

        if (!perDiem) {
          return { totalAmountCents: 0, isCalculating: false, error: 'Failed to calculate' }
        }

        // amount is in cents (BigInt), convert to number
        const totalAmountCents = perDiem.amount ? Number(perDiem.amount) : 0

        return {
          totalAmountCents,
          currency: perDiem.currency ?? 'EUR',
          isCalculating: false,
        }
      } catch {
        return { totalAmountCents: 0, isCalculating: false, error: 'Failed to calculate' }
      }
    },
    [calculateMutation]
  )

  return {
    calculate,
    loading,
  }
}
